import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  fetchRsvpDetails,
  fetchUserDetails,
  submitRsvpResponses,
} from '../BackendRoute';
import { useGuest } from '../GuestProvider';
import { useLanguage } from '../LanguageContext';
import { useSnackbar } from '../SnackbarContext';
import LoadingPage from '../components/LoadingPage';
import { locations, schedule } from '../pages/SchedulePage';
import { getTokenFromCookie } from '../utils/cookieUtils';
import { preloadResources } from '../utils/preload';
import IndividualChoiceStep from './IndividualChoiceStep';
import IndividualResponsesStep from './IndividualResponsesStep';
import MailingAddressStep from './MailingAddressStep';
import NameStep from './NameStep';
import PhysicalInvitationStep from './PhysicalInvitationStep';
import ReviewStep from './ReviewStep';
import SuccessPage from './SuccessPage';

const getEventDetails = (eventId, t) => {
  const event = schedule.find((e) => e.id === eventId);
  if (!event) return null;

  const location = locations.find((l) => l.id === event.mapQueryId);
  return {
    title: event.title,
    time: event.time,
    venue: event.venue,
    description: event.description,
    descriptionTitleKey: event.descriptionTitleKey,
    timeline: event.timeline,
    location: location
      ? {
          name: location.name,
          address: location.address,
          byCar: location.byCar,
          byTransport: location.byTransport,
          mapAddress: location.mapAddress,
        }
      : null,
  };
};

const slideVariants = {
  enter: (direction) => ({
    x: direction > 0 ? 50 : -50,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction) => ({
    x: direction < 0 ? 50 : -50,
    opacity: 0,
  }),
};

/**
 * For every step that declares `nullWhenSkipped` and is not currently visible,
 * set those per-guest fields to null. Pure function — returns the same object
 * reference if nothing changed.
 */
const applySkipCleanup = (data, steps) => {
  const skippedFields = steps
    .filter((s) => s.nullWhenSkipped && !s.isVisible(data))
    .flatMap((s) => s.nullWhenSkipped);

  if (skippedFields.length === 0) return data;

  const individualResponses = { ...(data.individualResponses || {}) };
  let changed = false;
  for (const guestId of Object.keys(individualResponses)) {
    for (const field of skippedFields) {
      if (individualResponses[guestId]?.[field] !== null) {
        individualResponses[guestId] = {
          ...individualResponses[guestId],
          [field]: null,
        };
        changed = true;
      }
    }
  }
  return changed ? { ...data, individualResponses } : data;
};

export default function RsvpForm() {
  const [isLoading, setIsLoading] = useState(true);
  const [resourcesLoaded, setResourcesLoaded] = useState(false);
  const [progress, setProgress] = useState(0);
  const { showSnackbar } = useSnackbar();
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(0);
  const [formData, setFormData] = useState({});

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const scrollRef = useRef(null);

  const { guestDetails, setGuestDetails } = useGuest();
  const [rsvpDetails, setRsvpDetails] = useState(null);
  const {
    t,
    isLoading: isLanguageLoading,
    setLocaleFromBackend,
  } = useLanguage();

  const navigate = useNavigate();

  const queryUserDetails = async () => {
    try {
      // Try to use token from cookie, fallback to id from URL
      const token = getTokenFromCookie();
      if (!token) {
        showSnackbar(t('main.error_no_token'), 'error');
        setIsLoading(false);
        return;
      }
      const identifier = token;

      if (identifier) {
        const resp = await fetchUserDetails(identifier, showSnackbar);
        setGuestDetails(resp.main_guest);
        setLocaleFromBackend(resp.main_guest?.default_language);
      }
      setIsLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    queryUserDetails();
    preloadResources((pct) => {
      setProgress(pct);
    }).then(() => {
      setResourcesLoaded(true);
    });
  }, []);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const token = getTokenFromCookie();
      if (!token) {
        showSnackbar(t('main.error_no_token'), 'error');
        setSubmitting(false);
        return;
      }
      await submitRsvpResponses(token, formData, showSnackbar);
      setSuccess(true);
    } catch (err) {
      setSuccess(false);
    } finally {
      setSubmitting(false);
    }
  };

  const queryRsvpDetails = async () => {
    try {
      const token = getTokenFromCookie();
      if (!token) {
        showSnackbar(t('main.error_no_token'), 'error');
        setIsLoading(false);
        return;
      }
      const identifier = token;

      const resp = await fetchRsvpDetails(identifier, showSnackbar);
      setRsvpDetails(resp);

      const formResponse = resp['responses'] || {};
      if (Object.keys(formResponse).length > 0) {
        const sanitizedResponses = {
          ...formResponse.individualResponses,
        };
        for (const guest of resp.all_guests) {
          const guestKey = Object.keys(sanitizedResponses).find(
            (key) => String(key) === String(guest.id),
          );
          if (guestKey && sanitizedResponses[guestKey]) {
            if (guest.is_morning === false) {
              sanitizedResponses[guestKey] = {
                ...sanitizedResponses[guestKey],
                isAttendingMorning: null,
              };
            }
            if (guest.is_tea_ceremony === false) {
              sanitizedResponses[guestKey] = {
                ...sanitizedResponses[guestKey],
                isAttendingTeaCeremony: null,
              };
            }
            if (guest.is_banquet === false) {
              sanitizedResponses[guestKey] = {
                ...sanitizedResponses[guestKey],
                isAttendingBanquet: null,
                banquetChoice: null,
              };
            }
          } else {
            sanitizedResponses[guest.id] = {
              isAttendingMorning: null,
              isAttendingTeaCeremony: null,
              isAttendingBanquet: null,
              dietaryRestrictions: null,
              banquetChoice: null,
            };
          }
        }
        formResponse.individualResponses = sanitizedResponses;
        setFormData(formResponse);
      } else {
        let individualResponses = {};
        for (const guest of resp.all_guests) {
          individualResponses[guest.id] = {
            isAttendingMorning: null,
            isAttendingTeaCeremony: null,
            isAttendingBanquet: null,
            dietaryRestrictions: null,
            banquetChoice: null,
          };
        }
        setFormData({
          individualResponses: individualResponses,
        });
      }

      setRsvpDetails({
        group_id: resp.group_id,
        all_guests: resp.all_guests,
      });
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    queryRsvpDetails();
  }, []);

  const { all_guests } = rsvpDetails || {};
  const guestNames = Array.isArray(all_guests)
    ? all_guests.map((guest) => guest.name).filter(Boolean)
    : [];

  const next = () => {
    const currentStep = step;
    setDirection(1);
    setFormData((prev) => applySkipCleanup(prev, allSteps));
    setStep((s) => (s === currentStep ? clampStep(s + 1) : s));
    scrollRef.current?.scrollTo({ top: 0, behavior: 'instant' });
  };
  const back = () => {
    const currentStep = step;
    setDirection(-1);
    setStep((s) => (s === currentStep ? clampStep(s - 1) : s));
  };
  const handleCancel = () => navigate('/');

  const isSelectionDisabled = (guestId, optionKey) => {
    if (!optionKey) return false;
    const guest = Array.isArray(all_guests)
      ? all_guests.find((g) => String(g.id) === String(guestId))
      : null;
    return guest ? guest[optionKey] === false : false;
  };

  const allSteps = [
    {
      key: 'name',
      isVisible: () => true,
      canContinue: () => true,
      element: (
        <NameStep
          formData={formData}
          setFormData={setFormData}
          allGuests={guestNames}
          mainGuestName={guestDetails?.name}
          onNext={next}
          onCancel={handleCancel}
        />
      ),
    },
    {
      key: 'morning',
      isVisible: () => {
        if (all_guests && all_guests.length === 1) {
          return all_guests[0].is_morning !== false;
        }
        return true;
      },
      canContinue: (data) => {
        const individualResponses = data.individualResponses || {};
        const guestIds = Object.keys(individualResponses);
        return (
          guestIds.length > 0 &&
          guestIds.every((guestId) => {
            const value = individualResponses[guestId]?.isAttendingMorning;
            const disabled = isSelectionDisabled(guestId, 'is_morning');
            return disabled ? value === null : value !== null;
          })
        );
      },
      element: (
        <IndividualResponsesStep
          formData={formData}
          setFormData={setFormData}
          allGuests={all_guests}
          responseKey="isAttendingMorning"
          guestOptionKey="is_morning"
          title={t('rsvp_form.matrimony_title')}
          yesLabel={t('rsvp_form.yes_label')}
          noLabel={t('rsvp_form.no_label')}
          onNext={next}
          onBack={back}
          eventDetails={getEventDetails('matrimony', t)}
        />
      ),
    },
    {
      key: 'teaCeremony',
      isVisible: () => {
        if (!all_guests) {
          return false;
        }
        if (all_guests && all_guests.length === 1) {
          return all_guests[0].is_tea_ceremony !== false;
        }
        return all_guests.some((guest) => guest.is_tea_ceremony !== false);
      },
      canContinue: (data) => {
        const individualResponses = data.individualResponses || {};
        const guestIds = Object.keys(individualResponses);
        return (
          guestIds.length > 0 &&
          guestIds.every((guestId) => {
            const value = individualResponses[guestId]?.isAttendingTeaCeremony;
            const disabled = isSelectionDisabled(guestId, 'is_tea_ceremony');
            return disabled ? value === null : value !== null;
          })
        );
      },
      element: (
        <IndividualResponsesStep
          formData={formData}
          setFormData={setFormData}
          allGuests={all_guests}
          responseKey="isAttendingTeaCeremony"
          guestOptionKey="is_tea_ceremony"
          title={t('rsvp_form.tea_title')}
          yesLabel={t('rsvp_form.yes_label')}
          noLabel={t('rsvp_form.no_label')}
          onNext={next}
          onBack={back}
          eventDetails={getEventDetails('tea_ceremony', t)}
          spanOptionKey="is_spectate_tea_ceremony"
          spanLabel={t('rsvp_form.tea_spectate_label')}
        />
      ),
    },
    {
      key: 'dinnerReception',
      isVisible: () => {
        if (!all_guests) {
          return false;
        }
        if (all_guests && all_guests.length === 1) {
          return all_guests[0].is_banquet !== false;
        }
        return all_guests.some((guest) => guest.is_banquet !== false);
      },
      canContinue: (data) => {
        const individualResponses = data.individualResponses || {};
        const guestIds = Object.keys(individualResponses);
        return (
          guestIds.length > 0 &&
          guestIds.every((guestId) => {
            const value = individualResponses[guestId]?.isAttendingBanquet;
            const disabled = isSelectionDisabled(guestId, 'is_banquet');
            return disabled ? value === null : value !== null;
          })
        );
      },
      element: (
        <IndividualResponsesStep
          formData={formData}
          setFormData={setFormData}
          allGuests={all_guests}
          responseKey="isAttendingBanquet"
          guestOptionKey="is_banquet"
          title={t('rsvp_form.dinner_title')}
          yesLabel={t('rsvp_form.yes_label')}
          noLabel={t('rsvp_form.no_label')}
          onNext={next}
          onBack={back}
          eventDetails={getEventDetails('dinner', t)}
        />
      ),
    },
    {
      key: 'banquetChoice',
      nullWhenSkipped: ['banquetChoice'],
      isVisible: (data) => {
        if (!all_guests) {
          return false;
        }
        return all_guests.some((guest) => {
          return (
            guest.is_banquet !== false &&
            data.individualResponses?.[guest.id]?.isAttendingBanquet === true
          );
        });
      },
      canContinue: (data) => {
        const individualResponses = data.individualResponses || {};
        const guestIds = Object.keys(individualResponses);
        return (
          guestIds.length > 0 &&
          guestIds.every((guestId) => {
            const guest = Array.isArray(all_guests)
              ? all_guests.find((g) => String(g.id) === String(guestId))
              : null;
            const isBanquetEnabled = guest ? guest.is_banquet !== false : false;
            const isAttending =
              individualResponses[guestId]?.isAttendingBanquet === true;
            const choice = individualResponses[guestId]?.banquetChoice;
            if (isBanquetEnabled && isAttending) {
              return choice !== null && choice !== undefined && choice !== '';
            }
            return true;
          })
        );
      },
      element: (
        <IndividualChoiceStep
          formData={formData}
          setFormData={setFormData}
          allGuests={all_guests}
          responseKey="banquetChoice"
          attendanceKey="isAttendingBanquet"
          guestOptionKey="is_banquet"
          title={t('rsvp_form.banquet_choice_title')}
          options={[
            { value: 'fish', label: t('rsvp_form.meat_fish') },
            { value: 'beef', label: t('rsvp_form.meat_beef') },
            {
              value: 'vegetarian',
              label: t('rsvp_form.meat_vegetarian'),
            },
            {
              value: 'halal',
              label: t('rsvp_form.meat_halal'),
            },
          ]}
          onNext={next}
          onBack={back}
        />
      ),
    },
    {
      key: 'dietary',
      nullWhenSkipped: ['dietaryRestrictions'],
      isVisible: (data) => {
        if (!all_guests) return false;
        // Show the step only if at least one guest is attending morning or banquet
        return all_guests.some((guest) => {
          const resp = data.individualResponses?.[guest.id];
          return (
            resp?.isAttendingMorning === true ||
            resp?.isAttendingBanquet === true
          );
        });
      },
      canContinue: (data) => {
        if (!all_guests) return false;
        const individualResponses = data.individualResponses || {};
        return all_guests.every((guest) => {
          const resp = individualResponses[guest.id];
          const isRelevant =
            resp?.isAttendingMorning === true ||
            resp?.isAttendingBanquet === true;
          if (!isRelevant) return true;
          return resp?.dietaryRestrictions !== null;
        });
      },
      element: (
        <IndividualChoiceStep
          formData={formData}
          setFormData={setFormData}
          allGuests={all_guests}
          responseKey="dietaryRestrictions"
          attendanceKey={['isAttendingMorning', 'isAttendingBanquet']}
          title={t('rsvp_form.dietary_title')}
          options={[
            { value: '', label: t('rsvp_form.diet_none') },
            { value: 'Halal', label: t('rsvp_form.diet_halal') },
            {
              value: 'Vegetarian',
              label: t('rsvp_form.diet_vegetarian'),
            },
            { value: 'Others', label: t('rsvp_form.diet_others') },
          ]}
          hasTextboxFor="Others"
          onNext={next}
          onBack={back}
        />
      ),
    },
    {
      key: 'review',
      isVisible: () => true,
      canContinue: () => !submitting,
      element: (
        <ReviewStep
          formData={formData}
          allGuests={all_guests}
          onBack={back}
          onSubmit={handleSubmit}
          submitting={submitting}
          success={success}
        />
      ),
    },
  ];

  const visibleSteps = allSteps.filter((step) => step.isVisible(formData));

  const clampStep = (value) =>
    Math.max(0, Math.min(visibleSteps.length - 1, value));

  useEffect(() => {
    if (step > visibleSteps.length - 1) {
      setStep(Math.max(0, visibleSteps.length - 1));
    }
  }, [visibleSteps.length, step]);

  const activeStep = visibleSteps[step];

  const getCanContinue = () => {
    if (!activeStep) return false;
    return activeStep.canContinue ? activeStep.canContinue(formData) : true;
  };

  if (isLoading || isLanguageLoading || !resourcesLoaded || !rsvpDetails) {
    return <LoadingPage progress={progress} />;
  }

  // Show success page after submission
  if (success === true) {
    return (
      <div className="h-dvh w-screen bg-wedding-blush text-wedding-heading flex flex-col overflow-hidden font-normal">
        <div className="flex-grow overflow-y-auto no-scrollbar px-8 py-8 md:py-12 max-w-xl mx-auto w-full">
          <SuccessPage />
        </div>
      </div>
    );
  }

  return (
    <div className="h-dvh w-screen bg-wedding-blush text-wedding-heading flex flex-col justify-between overflow-hidden font-normal">
      {/* Scrollable Step Content */}
      <div
        ref={scrollRef}
        className="flex-grow overflow-y-auto no-scrollbar px-8 py-8 md:py-12 max-w-xl mx-auto w-full rsvp-form-content relative"
      >
        <AnimatePresence mode="wait" initial={false} custom={direction}>
          <motion.div
            key={step}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: 'spring', stiffness: 300, damping: 30 },
              opacity: { duration: 0.2 },
            }}
            className="w-full"
          >
            {activeStep?.element}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Anchored bottom button bar */}
      <div className="border-t border-wedding-divider bg-wedding-blush py-4 px-6 max-w-xl mx-auto w-full flex gap-3 z-20">
        {step === 0 ? (
          <button onClick={handleCancel} className="btn-rsvp-nav">
            {t('rsvp_form.cancel')}
          </button>
        ) : (
          <button onClick={back} className="btn-rsvp-nav">
            {t('rsvp_form.back')}
          </button>
        )}

        {activeStep?.key === 'review' ? (
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="btn-rsvp-nav"
          >
            {submitting ? t('rsvp_form.submitting') : t('rsvp_form.submit')}
          </button>
        ) : (
          <button
            onClick={next}
            disabled={!getCanContinue()}
            className="btn-rsvp-nav"
          >
            {t('rsvp_form.next')}
          </button>
        )}
      </div>
    </div>
  );
}
